/**
 * Almacén local sobre IndexedDB.
 *
 * Sin dependencias: es una envoltura mínima de key/value. Sirve para guardar
 * la base de aeródromos, y es la semilla de la persistencia real (Fase 2), que
 * reemplazará a `window.storage` — la API de artifacts de Claude.ai que el
 * prototipo usa hoy y que no existe en un navegador.
 *
 * A diferencia de localStorage: guarda objetos sin serializar a mano, no tiene
 * el techo de ~5 MB, y no bloquea el hilo principal. Con miles de aeródromos
 * las dos últimas cosas importan.
 */

const BASE = "helicalc";
const VERSION = 1;
const DEPOSITO = "kv";

let conexion = null;

function abrir() {
  if (conexion) return conexion;

  conexion = new Promise((resolver, rechazar) => {
    if (typeof indexedDB === "undefined") {
      rechazar(new Error("Este navegador no permite almacenamiento local."));
      return;
    }

    const pedido = indexedDB.open(BASE, VERSION);

    pedido.onupgradeneeded = () => {
      const db = pedido.result;
      if (!db.objectStoreNames.contains(DEPOSITO)) {
        db.createObjectStore(DEPOSITO);
      }
    };

    /* ═══ SIN `onblocked`, LA APERTURA SE CUELGA SIN RESOLVER NI RECHAZAR ═══

       IndexedDB bloquea la apertura cuando otra pestaña tiene la base abierta
       con una versión distinta. Sin este manejador no llega `onsuccess` ni
       `onerror`: la promesa queda pendiente **para siempre**, y todo lo que
       espera al almacén espera en silencio.

       Es peor que un error: un error se ve. Esto no. */
    pedido.onblocked = () => {
      rechazar(new Error(
        "Hay otra pestaña de HeliCalc abierta con una versión distinta. "
        + "Cerrala y vuelve a cargar esta.",
      ));
    };

    pedido.onsuccess = () => resolver(pedido.result);
    pedido.onerror = () => rechazar(pedido.error);
  });

  /* ═══ UN PLAZO MÁXIMO ═══

     `onblocked` cubre el caso conocido. Este cubre los que no: un navegador
     con la base corrupta, o un estado que no dispara ningún manejador.

     Ocho segundos es mucho para arrancar y poco para que alguien se dé por
     vencido. */
  conexion = Promise.race([
    conexion,
    new Promise((_, rechazar) => setTimeout(() => rechazar(new Error(
      "El almacenamiento del navegador no responde. Puede haber otra pestaña "
      + "abierta, o el modo privado puede estar bloqueándolo.",
    )), 8000)),
  ]);

  /* ═══ UNA APERTURA RECHAZADA NO SE GUARDA ═══

     Estaba `conexion = new Promise(...)` a secas: si la apertura fallaba —un
     tropiezo al arrancar, la otra pestaña que después se cerró— esa promesa
     rechazada quedaba guardada y **todos los intentos siguientes devolvían el
     mismo rechazo**.

     La aplicación quedaba sin almacenamiento PARA SIEMPRE, aunque la causa ya
     no existiera. Soltarla deja que el siguiente intento vuelva a abrir. */
  conexion.catch(() => { conexion = null; });

  return conexion;
}

async function transaccion(modo, accion) {
  try {
    return await conTransaccion(modo, accion);
  } catch (e) {
    /* ═══ CONEXIÓN MUERTA: SOLTAR Y REABRIR ═══

       `InvalidStateError` significa que la conexión se cerró por debajo —el
       navegador la descartó, o hubo un cambio de versión—. Sin esto, todas las
       operaciones siguientes fallan igual y no hay forma de recuperarse sin
       recargar la página.

       Se intenta UNA vez: si la reapertura también falla, el problema es real
       y hay que decirlo, no seguir reintentando. */
    if (e?.name === "InvalidStateError" || /closing|closed/i.test(e?.message || "")) {
      conexion = null;
      return conTransaccion(modo, accion);
    }
    throw e;
  }
}

async function conTransaccion(modo, accion) {
  const db = await abrir();
  return new Promise((resolver, rechazar) => {
    const tx = db.transaction(DEPOSITO, modo);
    const deposito = tx.objectStore(DEPOSITO);
    const pedido = accion(deposito);

    tx.oncomplete = () => resolver(pedido?.result);
    tx.onerror = () => rechazar(tx.error);
    tx.onabort = () => rechazar(tx.error);
  });
}

/** Lee un valor. Devuelve undefined si la clave no existe. */
export function leer(clave) {
  return transaccion("readonly", (d) => d.get(clave));
}

/** Guarda un valor (cualquier objeto estructurable). */
export function guardar(clave, valor) {
  return transaccion("readwrite", (d) => d.put(valor, clave));
}

/** Borra una clave. */
export function borrar(clave) {
  return transaccion("readwrite", (d) => d.delete(clave));
}

/** Lista todas las claves guardadas. */
export function claves() {
  return transaccion("readonly", (d) => d.getAllKeys());
}

/** Tamaño aproximado en uso, si el navegador lo informa. */
export async function espacioUsado() {
  if (!navigator.storage?.estimate) return null;
  const { usage, quota } = await navigator.storage.estimate();
  return { usadoMB: usage / 1e6, cuotaMB: quota / 1e6 };
}
